import { useRef, useState } from "react";
import { api, uploadPhoto, PHOTO_SLOTS, ADDITIONAL_SLOT } from "../lib/api";
import { CaptureSlot } from "./CaptureSlot";

interface Photo { id: string; url: string; slot: string | null; kind: string }
interface Props {
  itemId: string;
  photos: Photo[];
  onChange: () => void | Promise<void>;
}

// Legacy photos (uploaded before slots existed) have slot=null. Infer a slot
// from the coarse kind so they still land in the right place in the manager.
const KIND_TO_SLOT: Record<string, string> = {
  price_tag: "thrift_tag",
  brand_tag: "brand_tag",
  item: "flat_front",
};
const slotOf = (p: Photo): string => p.slot ?? KIND_TO_SLOT[p.kind] ?? ADDITIONAL_SLOT;

/**
 * Full add / replace / remove surface for an item's photos, organized by the
 * standardized orientations (required first, then optional), plus an unlimited
 * "additional" bucket. Uploading into a standardized slot replaces whatever is
 * already there (the API enforces one photo per single slot).
 */
export function PhotoManager({ itemId, photos, onChange }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addInput = useRef<HTMLInputElement>(null);

  const bySlot = (s: string) => photos.find((p) => slotOf(p) === s);
  const additional = photos.filter((p) => slotOf(p) === ADDITIONAL_SLOT);

  async function remove(id: string) {
    if (!window.confirm("Remove this photo?")) return;
    setBusy(id);
    setError(null);
    try {
      await api.del(`/photos/${id}`);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove that photo");
    } finally {
      setBusy(null);
    }
  }

  async function addMany(files: FileList) {
    setAdding(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        await uploadPhoto(itemId, ADDITIONAL_SLOT, f);
      }
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setAdding(false);
      if (addInput.current) addInput.current.value = "";
    }
  }

  const renderSlot = (s: (typeof PHOTO_SLOTS)[number]) => {
    const existing = bySlot(s.slot);
    return (
      <div key={s.slot} className="pm-slot">
        <CaptureSlot
          itemId={itemId}
          slot={s.slot}
          caption={s.label}
          existingUrl={existing?.url ?? null}
          onUploaded={() => onChange()}
        />
        {existing ? (
          <button
            className="quiet danger pm-remove"
            disabled={busy === existing.id}
            onClick={() => void remove(existing.id)}
          >
            Remove
          </button>
        ) : (
          <span className={`pm-tag ${s.required ? "req" : ""}`}>
            {s.required ? "Required" : "Optional"} · tap to add
          </span>
        )}
      </div>
    );
  };

  const required = PHOTO_SLOTS.filter((s) => s.required);
  const optional = PHOTO_SLOTS.filter((s) => !s.required);

  return (
    <div className="pm">
      {error && <div className="notice">{error}</div>}

      <h3 className="pm-group">Required</h3>
      <div className="slots pm-grid">{required.map(renderSlot)}</div>

      <h3 className="pm-group">Optional — listing shots</h3>
      <div className="slots pm-grid">{optional.map(renderSlot)}</div>

      <h3 className="pm-group">Additional photos</h3>
      <div className="slots pm-grid">
        {additional.map((p) => (
          <div key={p.id} className="pm-slot">
            <span className="slot filled"><img src={p.url} alt="" /></span>
            <button
              className="quiet danger pm-remove"
              disabled={busy === p.id}
              onClick={() => void remove(p.id)}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="pm-slot">
          <button
            type="button"
            className="slot"
            style={{ width: "100%" }}
            onClick={() => addInput.current?.click()}
          >
            <span className="slot-caption">{adding ? "Uploading…" : "+ Add photos"}</span>
          </button>
          <input
            ref={addInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => e.target.files && void addMany(e.target.files)}
          />
        </div>
      </div>
    </div>
  );
}
